/**
 * @module app.core
 */

import { autoinject, bindingMode, TaskQueue } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { ICaseSearchFilters } from 'app/cm/services/case-service';
import { IMemberSearchFilters } from 'app/cd/services/member-service';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { fireEvent } from 'app/shared/lib/fire-events';
import { Toaster } from 'app/shared/lib/toaster';
import { ICaseSearchOptions } from 'app/cm/components/case/case-filters';
import { countMemberFilters } from 'app/cd/services/member-service';
import { countCaseFilters } from 'app/cm/services/case-service';
import { guid } from 'app/shared/lib/guid';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SetFocusEvent } from 'app/shared/lib/events';
import { isNullOrUndefined } from 'app/shared/lib/utilities';

interface IMemberSearchCustomEvent { memberSearchFilters: IMemberSearchFilters }
export type MemberSearchCustomEvent = CustomEvent<IMemberSearchCustomEvent>;

@autoinject()
export class MemberSearchFormCustomElement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public memberSearchFilters: IMemberSearchFilters = {};

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public tempMemberSearchFilters: IMemberSearchFilters = {};

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public defaultCollapsed: boolean = false;

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public defaultSearchCriteria: boolean = false;

    protected id: guid = guid();
    protected memberAvailableFilters = [
        'identifier.primary', 'identifier.medicaid', 'name.first', 'name.last', 'birthDateBetween',
        'identifier.socialSecurityNumber', 'gender', 'healthPlan', 'address.city', 'lineOfBusiness'
    ];


    ////****************** Element References ***************************************////
    protected $collapsible!: ICollapse;
    protected $searchCriteriaHeader!: HTMLElement;
    protected $currentCriteriaHeader!: HTMLElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly taskQueue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected attached(): void {
        if(this.defaultCollapsed) { return; }

        this.taskQueue.queueTask(() => {
            if(this.defaultSearchCriteria) {
                this.showSelectedCriteria();
            } else {
                this.showAvailableCriteria();
            }
        });
    }


    ////****************** Events ***************************************************////
    protected handleSubmit(event?: Event): void {
        if(!this.state.saving && !this.state.loading) {
            this.fireSearch();
        }
    }

    protected handleKeyDown(e: KeyboardEvent): boolean {
        if(e.keyCode === 13 && e.ctrlKey) {
            this.handleSubmit();
            return false;
        }
        return true;
    }

    ////****************** Functions ************************************************////
    public showAvailableCriteria(): void {
        this.$collapsible.open(0);
        this.$collapsible.close(1);
    }

    public showSelectedCriteria(): void {
        this.$collapsible.close(0);
        this.$collapsible.open(1);
    }

    protected fireSearch(): void {
        //check in advance b/c fireEvent is async and would just let showSelectedCriteria run no matter what
        const memberFilterCount = countMemberFilters(this.tempMemberSearchFilters);
        if(memberFilterCount === 0) {
            this.logger.warn('Invalid or missing search criteria');
            this.toaster.standard.searchNoCriteria();
            return;
        }

        if(isNullOrUndefined(this.tempMemberSearchFilters.dobBetweenEnd)){
            this.tempMemberSearchFilters.dobBetweenEnd = this.tempMemberSearchFilters.dobBetweenStart;
        }

        const detail: IMemberSearchCustomEvent = {
            memberSearchFilters: this.tempMemberSearchFilters
        }
        fireEvent(this.$element, 'search', detail);
        this.showSelectedCriteria();
        this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
    }

    public clear(returnFocus?: boolean): void {
        this.memberSearchFilters = {};
        this.tempMemberSearchFilters = {};
        this.showAvailableCriteria();
        fireEvent(this.$element, 'clear-results');

        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }
}
